import { useCallback, useEffect, useState } from 'react';
import { BACKEND_URL } from '../config';
import type { SystemStatus } from '../types/system';

const POLL_MS = 60_000;
const STORAGE_KEY = 'wt_neo_ack_build_id';

export function useSystemStatus() {
  const [status, setStatus] = useState<SystemStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  const checkBuildId = useCallback((buildId: string) => {
    const ack = localStorage.getItem(STORAGE_KEY);
    if (!ack) {
      localStorage.setItem(STORAGE_KEY, buildId);
      setUpdateAvailable(false);
      return;
    }
    setUpdateAvailable(ack !== buildId);
  }, []);

  const refresh = useCallback(async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/system/status`);
      if (!res.ok) return;
      const data = await res.json() as SystemStatus;
      setStatus(data);
      checkBuildId(data.buildId);
    } catch {
      // silencieux — bandeau optionnel
    } finally {
      setLoading(false);
    }
  }, [checkBuildId]);

  const acknowledgeUpdate = useCallback((buildId: string) => {
    localStorage.setItem(STORAGE_KEY, buildId);
    setUpdateAvailable(false);
  }, []);

  const reloadForUpdate = useCallback((buildId: string) => {
    localStorage.setItem(STORAGE_KEY, buildId);
    window.location.reload();
  }, []);

  useEffect(() => {
    void refresh();
    const id = window.setInterval(() => { void refresh(); }, POLL_MS);
    return () => window.clearInterval(id);
  }, [refresh]);

  return { status, loading, refresh, updateAvailable, acknowledgeUpdate, reloadForUpdate };
}
